import React from 'react';
import {
  ContainerCustomWidgetItemV2,
  ContainerWidgetItemV2,
  getObservabilityContext,
  useGetWidgetData,
} from '@leagueplatform/masonry-api';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import { logError } from '#utils/log-error.ts';
import { CustomWidget } from '#components/custom-widget/custom-widget.component.tsx';
import { MasonrySkeleton } from '#components/spinner/masonry-skeleton.component.tsx';
import { WidgetProviderFromWidgetTypeV2 } from '#components/widget-provider-from-type/widget-provider-from-widget-type-v2.component.tsx';
import { MasonryConfig } from '../../types/config.types';
import { AllWidgetsDisplayData } from '../../types/widget-display-data.types';
import { useGetConfig } from '../../hooks/use-get-config.hook';

type WidgetWrapperProps = {
  widget: ContainerWidgetItemV2;
};

export const MasonrySupportedWidget = ({ widget }: WidgetWrapperProps) => {
  const { apiVersion } = useGetConfig();
  const { loading } = widget;

  // Default widget data is fetched from get_widget_data API call
  const {
    data: widgetData,
    isLoading,
    error,
  } = useGetWidgetData<AllWidgetsDisplayData>(widget, apiVersion);

  if (isLoading) {
    return <MasonrySkeleton loading={loading} />;
  }

  if (error || !widgetData) {
    return null;
  }

  return <WidgetProviderFromWidgetTypeV2 data={widgetData} />;
};

export const WidgetWrapperV2 = ({
  widget,
  appId,
}: WidgetWrapperProps & { appId: MasonryConfig['appId'] }) => (
  // Returning null as fallback component because masonry should not display anything if a widget fails to render
  <ObservabilityErrorBoundary
    fallback={null}
    errorContext={getObservabilityContext({
      tags: {
        errorName: `Masonry - Error while rendering widget: ${widget.id}`,
        container: appId,
        widgetId: widget.id,
      },
    })}
    onError={(err: Error) => {
      logError(
        `Masonry - Error while rendering widget: ${widget.id} ${err.message}`,
      );
    }}
  >
    {widget.module !== 'default' && widget.custom_type ? (
      <CustomWidget widget={widget as ContainerCustomWidgetItemV2} />
    ) : (
      <MasonrySupportedWidget widget={widget} />
    )}
  </ObservabilityErrorBoundary>
);
